/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import { LoadingPage } from "src/components/loading/page";
import { NotFoundPage } from "src/pages/404";
import { PageHead } from "src/components/head/head";
import { RedirectPage } from "src/components/redirect/page";
import { SessionContext } from "src/components/session/session";
import { firstQueryValue } from "src/utils/links/query";
import { parseBrowserQuery } from "src/utils/links/query";
import { sanitizeRedirectLink } from "src/utils/links/links";
import "@rainbow-me/rainbowkit/styles.css";
import dunegql from "src/utils/dunegqlCodegen/dunegql";
import { Web3Provider } from "shared/web3/Web3Provider";
import { AuthPage } from "./AuthPage";
import { SignUp } from "./SignUp";
import { RegisterConfirm } from "./RegisterConfirm";
import { Login } from "./Login";
import { ResetPassword } from "./ResetPassword";
import { ResetConfirm } from "./ResetConfirm";
import { AlertsUpsell } from "./AlertsUpsell";

export interface Props {
  slug: string;
  preview: boolean;
}

/**
 * Renders several pages with path /auth/[slug]
 */
export const AuthPages: React.FC<Props> = (props) => {
  const { session, sessionLoading } = React.useContext(SessionContext);
  const next = firstQueryValue(parseBrowserQuery(), "next") || "/";
  const sanitizedNext = sanitizeRedirectLink(next);

  if (sessionLoading) {
    return <LoadingPage />;
  }

  if (session) {
    return <RedirectPage href={sanitizedNext || "/"} replace />;
  }

  const FormComponent =
    props.slug === "login"
      ? FormLogin
      : props.slug === "register"
      ? FormRegister
      : props.slug === "registerConfirm"
      ? EmailVerification
      : props.slug === "reset"
      ? FormReset
      : props.slug === "resetConfirm"
      ? FormResetConfirm
      : null;

  const pageTitle =
    props.slug === "login"
      ? "Sign in to Dune"
      : props.slug === "register"
      ? "Sign up for Dune"
      : props.slug === "registerConfirm"
      ? "Confirm email"
      : props.slug === "reset"
      ? "Reset password"
      : props.slug === "resetConfirm"
      ? "Confirm password reset"
      : undefined;

  if (!FormComponent) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageHead preview={props.preview} title={pageTitle} />
      <Web3Provider>
        <FormComponent {...props} />
      </Web3Provider>
    </>
  );
};

const FormLogin: React.FC<Props> = () => {
  return <AuthPage leftSide={<Login />} rightSide={<AlertsUpsell />} />;
};

const FormRegister: React.FC<Props> = () => {
  return <AuthPage leftSide={<SignUp />} rightSide={<AlertsUpsell />} />;
};

const EmailVerification: React.FC<Props> = () => {
  return (
    <AuthPage leftSide={<RegisterConfirm />} rightSide={<AlertsUpsell />} />
  );
};

dunegql`
mutation ConfirmSignup($username: String!, $code: String!) {
  confirmSignUp(username: $username, code: $code) {
    ok
  }
}
`;

const FormReset: React.FC<Props> = () => {
  return <AuthPage leftSide={<ResetPassword />} rightSide={<AlertsUpsell />} />;
};

const FormResetConfirm: React.FC<Props> = () => {
  return <AuthPage leftSide={<ResetConfirm />} rightSide={<AlertsUpsell />} />;
};
